import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AppError } from '@/core/errors/AppError';
import { UsuarioFormSchema, } from '@/data/dto/admin';
import { formatearFechaHora } from '@/core/utils/formatters';
import { useCrearUsuario, useEditarUsuario, useGruposAdmin } from '@/data/hooks/useAdmin';
import { useEmpleados, usePromotores } from '@/data/hooks/useCatalogos';
import { Button } from '@/shared/components/Button';
import { Checkbox } from '@/shared/components/Checkbox';
import { Field } from '@/shared/components/Field';
import { Input } from '@/shared/components/Input';
import { Select } from '@/shared/components/Select';
import { Combobox } from '@/shared/components/Combobox';
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, } from '@/shared/components/Modal';
import { useToast } from '@/shared/components/Toast';
export function UsuarioFormDialog({ open, onOpenChange, usuario }) {
    const modoEdicion = Boolean(usuario);
    const toast = useToast();
    const crear = useCrearUsuario();
    const editar = useEditarUsuario();
    const { data: grupos } = useGruposAdmin();
    const form = useForm({
        resolver: zodResolver(UsuarioFormSchema),
        defaultValues: {
            email: '',
            nombre: '',
            apellido: '',
            password: '',
            grupoIds: [],
            personaTipo: '',
            personaId: '',
        },
    });
    useEffect(() => {
        if (!open)
            return;
        form.reset(usuario
            ? {
                email: usuario.email,
                nombre: usuario.nombre,
                apellido: usuario.apellido,
                password: '',
                grupoIds: usuario.grupos.map((g) => g.id),
                personaTipo: usuario.personaTipo ?? '',
                personaId: usuario.personaId ?? '',
            }
            : {
                email: '',
                nombre: '',
                apellido: '',
                password: '',
                grupoIds: [],
                personaTipo: '',
                personaId: '',
            });
    }, [open, usuario, form]);
    const onSubmit = form.handleSubmit((values) => {
        if (!modoEdicion && (!values.password || values.password.length < 8)) {
            form.setError('password', { message: 'Mínimo 8 caracteres al crear.' });
            return;
        }
        const onSuccess = () => {
            toast.exito(modoEdicion ? 'Usuario actualizado' : 'Usuario creado');
            onOpenChange(false);
        };
        const onError = (err) => toast.error(err instanceof AppError ? err.mensaje : 'No pudimos guardar.');
        if (modoEdicion && usuario) {
            editar.mutate({ id: usuario.id, values }, { onSuccess, onError });
        }
        else {
            crear.mutate(values, { onSuccess, onError });
        }
    });
    const errors = form.formState.errors;
    const isPending = crear.isPending || editar.isPending;
    const grupoIds = form.watch('grupoIds');
    const personaTipo = form.watch('personaTipo') ?? '';
    const personaId = form.watch('personaId') ?? '';
    const { data: empleados } = useEmpleados();
    const { data: promotores } = usePromotores();
    // Un promotor "staff" (con eempleado vinculado) NO puede recibir el
    // grupo "Externo": ese grupo concede crear_reserva, capacidad reservada
    // a promotores realmente externos. Lo detectamos para deshabilitar la
    // opción y mostrar un helper.
    const promotorSeleccionadoEsStaff = useMemo(() => {
        if (personaTipo !== 'PROMOTOR' || !personaId)
            return false;
        const p = (promotores ?? []).find((x) => x.id === personaId);
        return Boolean(p?.esEmpleado);
    }, [personaTipo, personaId, promotores]);
    const opcionesPersona = useMemo(() => {
        if (personaTipo === 'INSTRUCTOR') {
            return (empleados ?? []).map((e) => ({
                value: e.id,
                label: `${e.nombre} ${e.apellido}`.trim(),
                hint: e.email ?? undefined,
            }));
        }
        if (personaTipo === 'PROMOTOR') {
            return (promotores ?? []).map((p) => ({
                value: p.id,
                label: [p.nombre, p.apellido].filter(Boolean).join(' ').trim() || p.nombre,
                hint: p.email ?? undefined,
            }));
        }
        return [];
    }, [personaTipo, empleados, promotores]);
    const grupoExternoId = useMemo(() => (grupos ?? []).find((g) => g.nombre === 'Externo')?.id, [grupos]);
    // Si pasa a ser promotor staff y tenía Externo asignado, lo quitamos.
    useEffect(() => {
        if (!promotorSeleccionadoEsStaff || !grupoExternoId)
            return;
        if (!grupoIds.includes(grupoExternoId))
            return;
        form.setValue('grupoIds', grupoIds.filter((id) => id !== grupoExternoId), { shouldDirty: true });
    }, [promotorSeleccionadoEsStaff, grupoExternoId, grupoIds, form]);
    const toggleGrupo = (gid) => {
        if (gid === grupoExternoId && promotorSeleccionadoEsStaff)
            return;
        const actual = new Set(grupoIds);
        if (actual.has(gid))
            actual.delete(gid);
        else
            actual.add(gid);
        form.setValue('grupoIds', Array.from(actual), { shouldDirty: true });
    };
    const onPersonaSeleccionada = (id) => {
        form.setValue('personaId', id, { shouldValidate: true, shouldDirty: true });
        if (!id)
            return;
        if (personaTipo === 'INSTRUCTOR') {
            const e = (empleados ?? []).find((x) => x.id === id);
            if (!e)
                return;
            form.setValue('nombre', e.nombre ?? '', { shouldDirty: true, shouldValidate: true });
            form.setValue('apellido', e.apellido ?? '', { shouldDirty: true, shouldValidate: true });
            form.setValue('email', e.email ?? '', { shouldDirty: true, shouldValidate: true });
            return;
        }
        if (personaTipo === 'PROMOTOR') {
            const p = (promotores ?? []).find((x) => x.id === id);
            if (!p)
                return;
            form.setValue('nombre', p.nombre ?? '', { shouldDirty: true, shouldValidate: true });
            // El catalogo de promotores no expone apellido; lo dejamos como esta.
            form.setValue('email', p.email ?? '', { shouldDirty: true, shouldValidate: true });
        }
    };
    return (_jsx(Modal, { open: open, onOpenChange: onOpenChange, children: _jsxs(ModalContent, { size: "md", children: [_jsx(ModalHeader, { titulo: modoEdicion ? 'Editar usuario' : 'Nuevo usuario', descripcion: modoEdicion
                        ? 'Dejá la contraseña vacía para no cambiarla.'
                        : 'La contraseña debe tener al menos 8 caracteres.' }), _jsxs("form", { onSubmit: onSubmit, noValidate: true, children: [_jsx(ModalBody, { children: _jsxs("div", { className: "grid grid-cols-1 gap-3 sm:grid-cols-2", children: [_jsxs("div", { className: "sm:col-span-2 rounded-md border border-border-subtle bg-surface-2 p-3", children: [_jsx("div", { className: "mb-2 text-[10px] font-semibold uppercase tracking-wider text-text-tertiary", children: "Vincular a una persona existente" }), _jsxs("div", { className: "grid grid-cols-1 gap-3 sm:grid-cols-2", children: [_jsx(Field, { label: "Tipo", error: errors.personaTipo?.message, helper: "Si seleccion\u00E1s, los datos se autocompletan.", children: _jsxs(Select, { ...form.register('personaTipo', {
                                                                onChange: () => {
                                                                    form.setValue('personaId', '', {
                                                                        shouldValidate: false,
                                                                        shouldDirty: true,
                                                                    });
                                                                },
                                                            }), children: [_jsx("option", { value: "", children: "Sin vincular" }), _jsx("option", { value: "INSTRUCTOR", children: "Instructor" }), _jsx("option", { value: "PROMOTOR", children: "Promotor" })] }) }), _jsx(Field, { label: personaTipo === 'PROMOTOR' ? 'Promotor' : 'Instructor', error: errors.personaId?.message, children: personaTipo === '' ? (_jsx("div", { className: "flex h-9 items-center rounded-md border border-border-subtle bg-surface-2 px-2 text-xs text-text-tertiary", children: "Eleg\u00ED un tipo primero." })) : (_jsx(Combobox, { value: personaId, onChange: onPersonaSeleccionada, options: opcionesPersona, placeholder: personaTipo === 'PROMOTOR' ? 'Elegí un promotor…' : 'Elegí un instructor…', searchPlaceholder: "Buscar por nombre o email\u2026", emptyText: "No hay coincidencias", clearable: true })) })] })] }), _jsx(Field, { label: "Nombre", error: errors.nombre?.message, required: true, children: _jsx(Input, { autoFocus: true, ...form.register('nombre') }) }), _jsx(Field, { label: "Apellido", error: errors.apellido?.message, required: true, children: _jsx(Input, { ...form.register('apellido') }) }), _jsx(Field, { label: "Email", error: errors.email?.message, className: "sm:col-span-2", required: true, children: _jsx(Input, { type: "email", ...form.register('email') }) }), _jsx(Field, { label: modoEdicion ? 'Nueva contraseña' : 'Contraseña', error: errors.password?.message, helper: modoEdicion ? 'Dejar vacío para mantener la actual' : 'Mínimo 8 caracteres', className: "sm:col-span-2", required: !modoEdicion, children: _jsx(Input, { type: "password", autoComplete: "new-password", ...form.register('password') }) }), _jsxs("div", { className: "sm:col-span-2", children: [_jsx("div", { className: "mb-1 text-[10px] font-semibold uppercase tracking-wider text-text-tertiary", children: "Grupos" }), _jsxs("div", { className: "flex flex-col gap-1 rounded-md border border-border-subtle p-2", children: [grupos?.map((g) => {
                                                        const deshabilitado = g.id === grupoExternoId && promotorSeleccionadoEsStaff;
                                                        return (_jsxs("label", { className: deshabilitado
                                                                ? 'flex items-center gap-2 rounded px-1.5 py-1 text-sm opacity-60'
                                                                : 'flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-sm hover:bg-surface-2', title: deshabilitado
                                                                ? 'Los promotores empleados no pueden estar en el grupo Externo (no pueden crear reservas).'
                                                                : undefined, children: [_jsx(Checkbox, { checked: grupoIds.includes(g.id), onChange: () => toggleGrupo(g.id), disabled: deshabilitado }), _jsx("span", { children: g.nombre }), g.sistema ? (_jsx("span", { className: "text-[10px] text-text-tertiary", children: "(sistema)" })) : null, deshabilitado ? (_jsx("span", { className: "text-[10px] text-text-tertiary", children: "no aplica a promotores empleados" })) : null] }, g.id));
                                                    }), !grupos?.length ? (_jsx("p", { className: "text-xs text-text-tertiary", children: "No hay grupos creados." })) : null] })] }), modoEdicion && usuario ? (_jsx(AuditoriaUsuario, { usuario: usuario, className: "sm:col-span-2" })) : null] }) }), _jsxs(ModalFooter, { children: [_jsx(Button, { type: "button", variant: "secondary", onClick: () => onOpenChange(false), disabled: isPending, children: "Cancelar" }), _jsx(Button, { type: "submit", cargando: isPending, children: modoEdicion ? 'Guardar' : 'Crear' })] })] })] }) }));
}
function AuditoriaUsuario({ usuario, className, }) {
    if (!usuario.creadoEn && !usuario.actualizadoEn)
        return null;
    return (_jsxs("div", { className: `${className ?? ''} mt-1 rounded-md border border-border-subtle bg-surface-2 px-3 py-2`, children: [_jsx("div", { className: "mb-1 text-[10px] font-semibold uppercase tracking-wider text-text-tertiary", children: "Auditor\u00EDa" }), _jsxs("dl", { className: "grid grid-cols-1 gap-1 text-[11px] sm:grid-cols-2", children: [usuario.creadoEn ? (_jsxs("div", { className: "flex flex-col", children: [_jsx("dt", { className: "text-text-tertiary", children: "Creado" }), _jsxs("dd", { children: [formatearFechaHora(usuario.creadoEn), usuario.creadoPor ? _jsx(ActorTag, { actor: usuario.creadoPor }) : null] })] })) : null, usuario.actualizadoEn ? (_jsxs("div", { className: "flex flex-col", children: [_jsx("dt", { className: "text-text-tertiary", children: "\u00DAltima edici\u00F3n" }), _jsxs("dd", { children: [formatearFechaHora(usuario.actualizadoEn), usuario.actualizadoPor ? _jsx(ActorTag, { actor: usuario.actualizadoPor }) : null] })] })) : null] })] }));
}
function ActorTag({ actor }) {
    return (_jsxs("span", { className: "ml-1 text-text-tertiary", title: actor.email, children: ["\u00B7 ", actor.nombre || actor.email] }));
}
